import { useIServiceModelNewApi } from "@/hooks/api/useIServiceApi";
import { useGetDivision } from "@/hooks/form-v3/useIServiceDivisionCode";
import { useGetServiceLabel } from "@/hooks/form-v3/useIServiceLabel";
import { useFailMsgBox } from "@/hooks/common/msg-box/useFailMsgBox";
import { parseFetchErrorBody } from "@/hooks/common/fetch/useFetch";
import { __DATA_FROM_ISERVICE__ } from "@/const/iserviceConst";

/**
 * model-number 组件的 iService 数据源逻辑
 * 仅当 state.conf.dataFrom === __DATA_FROM_ISERVICE__ 时生效，包含：
 * 1. 初始化：预加载全部 model 数据
 * 2. fetchIServiceSuggestions：调用 modelNew 接口获取联想建议（带同词缓存与 COMM-059 错误处理）
 * @param {Object} options
 * @param {Object} options.state - 当前组件的 csr state（含 conf.dataFrom/conf.serviceType）
 * @param {Object} options.currentActionState - 当前表单提交按钮的 state（用于获取 divisionCode）
 * @param {Object} options.autocompleteRef - Autocomplete 组件 ref（COMM-059 时触发失焦）
 * @returns {{ isIService: boolean, fetchIServiceSuggestions: Function }}
 */
export const useIServiceModelNumber = ({ state, currentActionState, autocompleteRef }) => {
  const isIService = state.conf?.dataFrom === __DATA_FROM_ISERVICE__;

  // 上一次请求的词与结果缓存，queryString 未变化时不重复调用接口
  let lastQueryString = null;
  let lastSuggestions = [];

  // iService新接口：fetchSuggestions时调用modelNew API
  const fetchIServiceSuggestions = async (queryString, cb) => {
    // 空值统一为''，避免 undefined/null/'' 造成重复请求
    const query = queryString || '';
    // 如果queryString没有变化，不重复调用接口，直接复用上次的结果
    if (query === lastQueryString) {
      cb(lastSuggestions);
      return;
    }
    lastQueryString = query;

    const { divisionCode } = await useGetDivision(currentActionState);
    const { data, error } = await useIServiceModelNewApi({
      divisionCode,
      modelName: query,
      serviceType: state.conf.serviceType,
    }, { showErrorTips: false });

    // error.value 非 null 即代表接口失败（useGwsFetch 已把响应体序列化进 error message）
    if (error.value !== null) {
      const { resultCode } = parseFetchErrorBody(error.value);
      // 当接口返回 COMM-059 错误时，显示错误提示并让组件失焦
      if (resultCode === 'COMM-059') {
        // useGetServiceLabel 内部已缓存 Promise，报错时按需获取错误文案即可，无需存入 store
        const labelData = await useGetServiceLabel().catch(() => null);
        const errorMsg = labelData?.error_comm_059_SR_modelcheck?.label;
        if (errorMsg) {
          useFailMsgBox(errorMsg);
        }
        autocompleteRef.value?.blur();
      }
      lastSuggestions = [];
      cb(lastSuggestions);
      return;
    }
    lastSuggestions = (data.value || []).map(item => ({
      ...item,
      value: item.customerModel
    }));
    cb(lastSuggestions);
  };

  if (isIService) {
    // 初始化时先触发一次fetchSuggestions，预加载全部model数据（结果缓存于lastSuggestions）
    fetchIServiceSuggestions('', () => {});
  }

  return {
    isIService,
    fetchIServiceSuggestions,
  };
};
